import { JSX, Suspense, useMemo } from 'react';
import { Routes, Route, Navigate, Outlet, BrowserRouter } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from "redux-persist/integration/react";
import { LinearProgress, Slide } from '@mui/material';
import { SnackbarProvider } from "notistack";

import { persistor, RootState, store } from './state/store';
import { SignalRProvider } from "./components/Providers/SignalRProvider";
import { GlobalModalProvider } from './components/Providers/GlobalModalProvider';
import Header from './components/Header/Header';
import Login from './pages/Login/Login';
import NotFound from "./pages/NotFound/NotFound";
import { API_BASE_URL } from "./config";
import { TimezoneProvider } from "./components/Providers/TimezoneProvider";
import useShiftBoundarySync from "./components/Hooks/useShiftBoundarySync";
import { UserDto } from "./DTOs/User/UserDto";
import { hasAllRoles, hasAnyRole } from './utilities/helpers/roleUtilities';
import { AppRouteConfig, createAppRoutes } from "./utilities/appRoutes";
import { RoleConfig } from "./DTOs/Settings/RoleConfigDto";
import ShiftLoginBootstrapper from "./components/Providers/ShiftLoginBootstrapper";
import GlobalNotificationListener from './components/Providers/GlobalNotificationListener';
import ErrorScreen from './components/ErrorScreen/ErrorScreen';
import { useSignalRContext } from './components/Contexts/SignalRContext';
import { useApplyTheme } from './components/Hooks/useApplyTheme';
import { resolveAppShellState } from './utilities/helpers/appShellState';
import ConnectionBanner from './components/ConnectionBanner/ConnectionBanner';
import { AppMuiProvider } from './components/Providers/MuiThemeProvider';
import ShiftLockBanner from './components/ShiftLockBanner/ShiftLockBanner';
import OfflineBanner from './components/OfflineBanner/OfflineBanner';
import OfflineGuard from './components/OfflineGuard/OfflineGuard';
import SyncIssuesBanner from './components/SyncIssuesBanner/SyncIssuesBanner';
import { useSyncWorker } from './offline/useSyncWorker'
import { useShiftBands } from './offline/useShiftBands';
import { usePrefetchCriticalPages } from './offline/usePrefetchCriticalPages';

interface ProtectedRouteProps {
  user: UserDto | null;
  access?: string[];
  children: JSX.Element;
  isAuthenticated?: boolean;
  accessAll?: string[];
}

function ProtectedRoute(
  {
    user,
    access,
    children,
    isAuthenticated,
    accessAll
  }: ProtectedRouteProps) {
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (access && !user) return <LinearProgress />;

  const hasPermission =
    (!access || hasAnyRole(user, access)) &&
    (!accessAll || hasAllRoles(user, accessAll));

  if (!hasPermission) {
    return <Navigate to="/" />;
  }

  return children;
}

function generateRoutes(routes: AppRouteConfig[], user: UserDto | null, isAuthenticated: boolean): JSX.Element[] {
  return routes.map((route, i) => {
    const element = route.protected
      ? (
        <ProtectedRoute
          key={route.path}
          user={user}
          access={route.access}
          isAuthenticated={isAuthenticated}
          accessAll={route.accessAll}
        >
          {route.element ?? <Outlet />}
        </ProtectedRoute>
      )
      : route.element ?? <Outlet />;

    return (
      <Route
        key={route.path || i}
        path={route.path}
        element={element}
      >
        {route.children && generateRoutes(route.children, user, isAuthenticated)}
      </Route>
    );
  });
}

function AppLayout() {
  useShiftBoundarySync();
  useApplyTheme();
  useSyncWorker();
  useShiftBands();
  usePrefetchCriticalPages();
  const { isConnectionLoading } = useSignalRContext();
  const { isAuthenticated, isAuthLoading, error } = useSelector((state: RootState) => state.authReducer);


  const shellState = resolveAppShellState({// Note: only auth errors show the blocking ErrorScreen; a live-connection problem keeps the page up and is surfaced via the banner below instead.
    isAuthLoading,
    isAuthenticated,
    isConnectionLoading,
    hasAuthError: !!error,
  });
  const isMetaDataLoading = shellState === "loading";

  return (
    <AppMuiProvider>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
        autoHideDuration={3000}
        dense
        preventDuplicate
      >
        <ConnectionBanner />
        <GlobalModalProvider>
          <div className="h-screen flex flex-col overflow-hidden">
            <Header />
            <OfflineBanner />
            <SyncIssuesBanner />
            <ShiftLockBanner />
            <main className="flex-1 overflow-y-auto bg-theme-bg transition-colors min-h-0">
              {shellState === "loading" ? (
                <LinearProgress />
              ) : shellState === "error" ? (
                <ErrorScreen />
              ) : (
                <>
                  <GlobalNotificationListener />
                  <OfflineGuard>
                    <Suspense fallback={<LinearProgress />}>
                      <Outlet />
                    </Suspense>
                  </OfflineGuard>
                </>
              )}
            </main>
          </div>
        </GlobalModalProvider>
      </SnackbarProvider>
    </AppMuiProvider>
  );
}

// TODO: merge material type & class, buckets + bucket factors, Plant + Plant Buckets, pits and pit stages
function AppRouter({ isAuthenticated, user, roleConfig }: { isAuthenticated: boolean, user: UserDto | null, roleConfig: RoleConfig | null }) {
  const routes = useMemo(
    () => roleConfig ? createAppRoutes(roleConfig) : [],
    [roleConfig]
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          isAuthenticated
            ? <Navigate to="/" replace />
            : <Login />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
            <AppLayout />
          </ProtectedRoute>
        }>
          {generateRoutes(routes, user, isAuthenticated)}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

const App: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.authReducer.isAuthenticated);
  const user = useSelector((state: RootState) => state.authReducer.user);
  const roleConfig = useSelector((state: RootState) => state.authReducer.roleConfig);

  return (
    <Provider store={store}>
      <PersistGate loading={<LinearProgress />} persistor={persistor}>
        <TimezoneProvider user={user}>
          <SignalRProvider hubUrl={`${API_BASE_URL}/liveUpdateHub`}>
            <ShiftLoginBootstrapper />
            <AppRouter isAuthenticated={isAuthenticated} user={user} roleConfig={roleConfig} />
          </SignalRProvider>
        </TimezoneProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
